import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Compass, ArrowRight, AlertCircle } from "lucide-react";

export default function RegisterPage() {
    const navigate = useNavigate();
    const { register, formatApiError } = useAuth();
    const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await register(form);
            navigate("/dashboard");
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-sand">
            <div className="w-full max-w-lg">
                <div className="flex items-center gap-2.5 mb-10 justify-center">
                    <div className="w-9 h-9 rounded-md bg-navy flex items-center justify-center">
                        <Compass className="w-5 h-5 text-sand" strokeWidth={1.75} />
                    </div>
                    <div className="leading-none">
                        <div className="text-[15px] font-bold tracking-tight text-ink" style={{ fontFamily: "Space Grotesk" }}>Pro-Travel Planner</div>
                    </div>
                </div>

                <div className="bg-white border border-sand-300 rounded-lg shadow-card p-8 lg:p-10 relative overflow-hidden">
                    <span className="pt-accent-bar" />
                    <p className="pt-eyebrow mb-3">Registrierung · Berater</p>
                    <h1 className="pt-h2 mb-6" data-testid="register-heading">Berater-Konto erstellen</h1>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Name</label>
                            <input required value={form.name} onChange={update("name")} data-testid="register-name-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-navy" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Agentur (optional)</label>
                            <input value={form.company} onChange={update("company")} data-testid="register-company-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-navy" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">E-Mail</label>
                            <input type="email" required value={form.email} onChange={update("email")} data-testid="register-email-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-navy" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Passwort (min. 6)</label>
                            <input type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="register-password-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-navy" />
                        </div>

                        {err && (
                            <div className="flex items-start gap-2 p-3 bg-terracotta/10 border border-terracotta/30 rounded-md" data-testid="register-error">
                                <AlertCircle className="w-4 h-4 text-terracotta mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-terracotta-hover">{err}</p>
                            </div>
                        )}

                        <button type="submit" disabled={busy} data-testid="register-submit-button"
                            className="group w-full bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-6 py-3.5 font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                            {busy ? "Konto wird erstellt…" : "Konto erstellen"}
                            {!busy && <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />}
                        </button>
                    </form>

                    <p className="mt-6 text-sm text-ink-secondary text-center">
                        Bereits registriert?{" "}
                        <Link to="/login" className="text-navy hover:text-navy-hover font-semibold underline-offset-4 hover:underline" data-testid="link-to-login">
                            Einloggen
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
